import React, {useCallback, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';

import {
  Accordion as BaseAccordion,
  Box,
  Button,
  Flex,
  Heading,
  Skeleton,
  SkeletonText,
  Text,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Component as ContentfulComponent} from '@amblique/king-living/app/components/contentful/component';
import {PlusIcon, MinusIcon} from '@amblique/king-living/app/components/icons';

const buildClassName = (...parts) => parts.filter(Boolean).join(' ');

/**
 * @override
 * AccordionPanel component
 * - Container for multiple accordions with optional media (Image or Video)
 * - Responsive layout: desktop 2 columns (media right), mobile stacked
 * - Uses accordion order from API response
 *
 * @param {Object} props - Component props
 * @param {Contentful.AccordionPanel} props.component - AccordionPanel component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @returns {React.ReactElement}
 */
const AccordionPanel = ({component, isComplete = true, isFetching = false, className, ...props}) => {
  const styles = useMultiStyleConfig('contentful/accordion-panel', {component, isComplete, isFetching});

  const accordions = component.accordionsCollection?.items || [];

  // Create stable key for accordions to detect changes
  const accordionsKey = useMemo(() => {
    return accordions.map((a) => a.sys?.id).filter(Boolean).join(',');
  }, [accordions]);

  const [expandedIndices, setExpandedIndices] = useState([]);

  // Reset expanded indices when accordions change
  useEffect(() => {
    setExpandedIndices([]);
  }, [accordionsKey]);

  // Handle toggle all button - memoized to prevent unnecessary re-renders
  const handleToggleAll = useCallback(() => {
    setExpandedIndices((prevIndices) => {
      if (prevIndices.length === accordions.length) {
        return [];
      }

      return accordions.map((_, index) => index);
    });
  }, [accordions]);

  if (!component.title && accordions.length === 0 && isComplete) {
    return null;
  }

  const isAllExpanded = expandedIndices.length === accordions.length && accordions.length > 0;

  return (
    <Box
      {...styles.root}
      {...props}
      className={buildClassName('contentful-accordion-panel', className)}
    >
      {!isComplete && isFetching ? (
        <>
          <Skeleton height="48px" mb={4} width="60%" />
          <SkeletonText noOfLines={5} spacing={4} />
        </>
      ) : (
        <>
          {(component.title || (component.showToggleAll === true && accordions.length > 0)) && (
            <Flex
              className="contentful-accordion-panel__header"
              {...styles.header}
              justifyContent={component.title ? 'space-between' : 'flex-end'}
            >
              {component.title && (
                <Heading as="h2" className="contentful-accordion-panel__title" {...styles.title}>
                  {component.title}
                </Heading>
              )}
              {component.showToggleAll === true && accordions.length > 0 && (
                <Button
                  className="contentful-accordion-panel__toggle-all"
                  {...styles.toggleAllButton}
                  aria-controls={accordions.map((a) => `accordion-${a.sys?.id}-panel`).filter(Boolean).join(' ')}
                  aria-label={isAllExpanded ? 'Collapse All' : 'Expand All'}
                  variant="ghost"
                  onClick={handleToggleAll}
                >
                  <Text className="contentful-accordion-panel__toggle-all-text" {...styles.toggleAllButtonText}>
                    {isAllExpanded ? 'Collapse all' : 'Expand all'}
                  </Text>
                  <Box className="contentful-accordion-panel__toggle-all-icon" {...styles.toggleAllButtonIcon}>
                    {isAllExpanded ? (
                      <MinusIcon height="100%" width="100%" />
                    ) : (
                      <PlusIcon height="100%" width="100%" />
                    )}
                  </Box>
                </Button>
              )}
            </Flex>
          )}

          {accordions.length > 0 && (
            <Flex className="contentful-accordion-panel__content" {...styles.content} flexDirection={{base: 'column', lg: 'row'}}>
              <Box className="contentful-accordion-panel__accordions" {...styles.accordions}>
                <BaseAccordion
                  className="contentful-accordion-panel__accordion"
                  index={expandedIndices}
                  {...styles.accordion}
                  allowMultiple
                  allowToggle
                  onChange={setExpandedIndices}
                >
                  {accordions.map((accordion) => (
                    <ContentfulComponent
                      key={`accordion[${accordion.sys?.id}]`}
                      component={accordion}
                    />
                  ))}
                </BaseAccordion>
              </Box>

              {Boolean(component.media) && (
                <Box className="contentful-accordion-panel__media" {...styles.mediaContainer}>
                  <Box className="contentful-accordion-panel__media-wrapper" {...styles.mediaWrapper}>
                    <ContentfulComponent component={component.media} />
                  </Box>
                </Box>
              )}
            </Flex>
          )}
        </>
      )}
    </Box>
  );
};

AccordionPanel.displayName = 'AccordionPanel';
AccordionPanel.propTypes = {
  className: PropTypes.string,
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};

// Memoize component to prevent unnecessary re-renders
const MemoizedAccordionPanel = React.memo(AccordionPanel);

MemoizedAccordionPanel.displayName = 'AccordionPanel';

export default MemoizedAccordionPanel;
export {MemoizedAccordionPanel as AccordionPanel};
